import React, { ChangeEvent, LegacyRef, useEffect, useMemo, useRef, useState } from "react";

import { Grid, IconButton } from "@mui/material";
import {
  Add as AddIcon,
  Home as HomeIcon,
  KeyboardBackspace as KeyboardBackspaceIcon,
  Publish as PublishIcon,
  Search as SearchIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { File as IFile } from "../../../../models/file";
import { Folder as IFolder } from "../../../../models/folder";
import { convertFilesToBatch } from "../../../../util/convertEventToFile";
import Button, { Color } from "../../../Form/Button";
import Input from "../../../Form/Input";
import ImageCarouselModal, { ImageCarouselModalHandle } from "../../../ImageCarouselModal";
import SkeletonComponent from "../../Skeleton";
import { NotificationToast } from "../../Toast/NotificationToast";
import FileComponent from "./File";
import Folder from "./Folder";
import { FoldersListSectionTitle } from "./FoldersListSectionTitle";
import useStyles from "./styles";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILES_PER_UPLOAD = 10;

type Props = {
  folders?: IFolder[];
  type?: string;
  onChangeFolders?: (folders?: IFolder[]) => void;
  allowedExtensions?: string[];
  onDeleteFolder?: (folder?: IFolder) => void;
  onDeleteFile?: (file?: IFile) => void;
  loading?: boolean;
  isOnViewMode?: boolean;
  canAdd?: boolean;
};

const ListFolders = (props: Props) => {
  const {
    folders,
    type,
    onChangeFolders,
    allowedExtensions,
    onDeleteFolder,
    onDeleteFile,
    loading,
    isOnViewMode = false,
    canAdd = true,
  } = props;

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const showImgModalRef = useRef<ImageCarouselModalHandle>(null);

  const [navigation, setNavigation] = useState<string[]>([]);

  const [actualFolder, setActualFolder] = useState<IFolder>();

  const [search, setSearch] = useState<string>();
  const [inputSearch, setInputSearch] = useState<string>();

  const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false);

  const hiddenFileInput: LegacyRef<HTMLInputElement> = useRef(null);

  const chooseFile = () => {
    if (hiddenFileInput && hiddenFileInput?.current) hiddenFileInput?.current?.click();
  };

  const createNewFilesInBatch = (files: IFile[]) => {
    const foldersUpdated = [...(folders || [])];
    let targetFolderName: string;
    let successCount = 0;

    if (navigation && navigation?.length > 0) {
      const length = navigation?.length ?? 1;
      targetFolderName = navigation[length - 1];
    }

    files.forEach((file) => {
      const wasAdded = createFolderOrFile(foldersUpdated, targetFolderName, undefined, file);

      if (wasAdded) {
        successCount += 1;
      }
    });

    if (onChangeFolders) {
      onChangeFolders(foldersUpdated);
    }

    return successCount;
  };

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (files.length > MAX_FILES_PER_UPLOAD) {
      NotificationToast.warning(translate("maximum10FilesPerUpload"));

      event.target.value = "";

      return;
    }

    const overSizedFiles = Array.from(files).filter((file) => file.size > MAX_FILE_SIZE);

    if (overSizedFiles.length > 0) {
      NotificationToast.warning(
        `${translate("filesExceedSizeLimit")}:\n${overSizedFiles
          .map((file) => file.name)
          .join(", ")}`
      );

      event.target.value = "";

      return;
    }

    const results = await convertFilesToBatch(files, translate, allowedExtensions);

    let successfullyAddedCount = 0;

    if (results.successfulFiles.length > 0) {
      successfullyAddedCount = createNewFilesInBatch(results.successfulFiles);
    }

    if (results.failedFiles.length > 0) {
      const errorMessage = results.failedFiles
        .map((failFile) => `${failFile.fileName}: ${failFile.error}`)
        .join("\n");

      NotificationToast.error(
        `${translate("someFilesFailedToUpload")} (${results.failedFiles.length}/${
          files.length
        }):\n\n${errorMessage}`
      );
    }

    if (files.length === successfullyAddedCount && results.failedFiles.length === 0) {
      NotificationToast.success(translate("allFilesUploadedSuccessfully"));
    }

    event.target.value = "";
  };

  useEffect(() => {
    getActualFolder();
  }, [navigation, folders]);

  const onNavigateToFolder = (name: string) => {
    setNavigation((prev) => [...prev, name]);
    setSearch("");
    setIsCreatingFolder(false);
  };

  const onBackFolder = () => {
    const navigationUpdated = [...navigation];

    if (navigationUpdated && navigationUpdated?.length > 0) navigationUpdated.pop();
    setNavigation(navigationUpdated);
  };

  const getActualFolder = () => {
    let folder: IFolder | undefined;

    navigation.forEach((roomName) => {
      const foldersToSearch = folder?.folders || folders;
      const folderFind = foldersToSearch?.find((folder) => folder?.name === roomName);

      if (folderFind) folder = folderFind;
    });

    setActualFolder(folder);
  };

  const isValidFileOrFolder = (
    actualArray: IFolder[] | IFile[],
    folderOrFile?: IFolder | IFile,
    indexToIgnore?: number
  ) => {
    let isValid = !!folderOrFile?.name;

    if (actualArray && actualArray?.length > 0) {
      actualArray?.forEach((item, index) => {
        if (
          item?.name &&
          folderOrFile &&
          item?.name?.toLowerCase() === folderOrFile?.name?.toLowerCase()
        ) {
          if (indexToIgnore === undefined) {
            isValid = false;
          } else if (indexToIgnore !== index) {
            isValid = false;
          }
        }
      });
    }

    return isValid;
  };

  const editSpecificFolder = (
    folders?: IFolder[],
    nameFolder?: string,
    folderToUpdate?: IFolder
  ) => {
    if (folders && folders?.length > 0) {
      folders?.forEach((folder, index) => {
        if (
          folder?.name &&
          nameFolder &&
          folder?.name?.toLowerCase() === nameFolder.toLowerCase()
        ) {
          if (folderToUpdate) {
            if (isValidFileOrFolder(folders, folderToUpdate, index)) {
              folders[index] = { ...folder, ...folderToUpdate };
            } else {
              NotificationToast.warning(translate("anotherFolderWithThisNameAlreadyExists"));
            }
          } else if (onDeleteFolder) {
            onDeleteFolder(folders[index]);
            delete folders[index];
          }
        } else {
          folder.folders = editSpecificFolder(folder?.folders, nameFolder, folderToUpdate);
        }
      });
    }

    return folders?.filter(Boolean);
  };

  const editSpecificFile = (
    folders?: IFolder[],
    nameFolder?: string,
    nameFile?: string,
    fileToUpdate?: IFile
  ) => {
    if (folders && folders?.length > 0) {
      folders?.forEach((folder) => {
        if (
          folder?.name &&
          nameFolder &&
          folder?.name?.toLowerCase() === nameFolder.toLowerCase()
        ) {
          if (folder?.files && folder?.files?.length > 0) {
            folder.files.forEach((file, indexFile) => {
              if (file?.name && nameFile && file?.name?.toLowerCase() === nameFile.toLowerCase()) {
                if (fileToUpdate) {
                  if (folder?.files) {
                    if (isValidFileOrFolder(folder.files, fileToUpdate, indexFile)) {
                      folder.files[indexFile] = { ...file, ...fileToUpdate };
                    } else {
                      NotificationToast.warning(translate("fileAlreadyExistsInThisFolder"));
                    }
                  }
                } else if (folder?.files) {
                  if (onDeleteFile) {
                    onDeleteFile(folder.files[indexFile]);
                    delete folder.files[indexFile];
                  }
                }
              }
            });
            folder.files = folder?.files?.filter(Boolean);
          }
        } else {
          editSpecificFile(folder?.folders, nameFolder, nameFile, fileToUpdate);
        }
      });
    }
  };

  const createFolderOrFile = (
    folders?: IFolder[],
    nameFolder?: string,
    newFolder?: IFolder,
    newFile?: IFile
  ): boolean => {
    if (nameFolder) {
      if (folders && folders?.length > 0 && (newFolder || newFile)) {
        let type: "folders" | "files" | null = null;
        let wasAdded = false;

        if (newFolder) type = "folders";
        else if (newFile) type = "files";

        if (type) {
          folders?.forEach((folder, index) => {
            if (folder?.name && nameFolder && nameFolder === folder?.name) {
              let actualArray: IFolder[] | undefined = [];

              if (type !== null && folder?.[type]) actualArray = folder?.[type];

              if (actualArray) {
                let folderOrFile: IFolder | IFile | undefined;

                if (type === "folders") folderOrFile = newFolder;
                else if (type === "files") folderOrFile = newFile;

                if (folderOrFile && type !== null) {
                  const objectByType = {
                    [type]: [folderOrFile, ...actualArray],
                  };

                  if (isValidFileOrFolder(actualArray, folderOrFile)) {
                    folders[index] = { ...folder, ...objectByType };
                    setIsCreatingFolder(false);
                    wasAdded = true;
                  } else if (type === "folders") {
                    NotificationToast.warning(translate("anotherFolderWithThisNameAlreadyExists"));
                  } else if (type === "files") {
                    NotificationToast.warning(translate("fileAlreadyExistsInThisFolder"));
                  }
                }
              }
            } else {
              const childResult = createFolderOrFile(folder?.folders, nameFolder, newFolder, newFile);

              if (childResult) {
                wasAdded = true;
              }
            }
          });
        }
        return wasAdded;
      }
    } else if (folders && newFolder) {
      if (isValidFileOrFolder(folders, newFolder)) {
        folders?.unshift({ ...newFolder });
        setIsCreatingFolder(false);

        return true;
      } else {
        NotificationToast.warning(translate("anotherFolderWithThisNameAlreadyExists"));
      }
    }

    return false;
  };

  const createNewFolderOrFile = (folder?: IFolder, file?: IFile) => {
    const foldersUpdated = [...(folders || [])];
    let name;

    if (navigation && navigation?.length > 0) {
      const length = navigation?.length ?? 1;

      name = navigation[length - 1];
    }

    createFolderOrFile(foldersUpdated, name, folder, file);

    if (onChangeFolders) {
      onChangeFolders(foldersUpdated);
    }
  };

  const onEditFolderOrFile = (
    nameFolder: string,
    nameFile?: string,
    folder?: IFolder,
    file?: IFile
  ) => {
    const foldersUpdated = [...(folders || [])];

    if (nameFile) {
      editSpecificFile(foldersUpdated, nameFolder, nameFile, file);
    } else {
      editSpecificFolder(foldersUpdated, nameFolder, folder);
    }
    if (onChangeFolders) onChangeFolders(foldersUpdated.filter(Boolean));
  };

  const getHistoryOfNavigation = () => {
    return `/ ${navigation.join(" / ")}`;
  };

  const renderFolders = useMemo(() => {
    const foldersToList = actualFolder ? actualFolder?.folders : folders;

    return (
      <SkeletonComponent isLoading={!!loading}>
        <>
          {foldersToList
            ?.filter((folder) => {
              if (search && folder?.name)
                return folder?.name?.trim().toLowerCase().includes(search.trim().toLowerCase());

              return true;
            })
            ?.map((folder) => {
              const uniqueKey = folder.id ?? folder.name ?? folder.createdAt?.toString();

              return (
                <Folder
                  isOnViewMode={isOnViewMode}
                  key={uniqueKey}
                  folder={folder}
                  onDelete={() => folder?.name && onEditFolderOrFile(folder?.name)}
                  onOpen={() => folder?.name && onNavigateToFolder(folder?.name)}
                  onEdit={(folderUpdated) =>
                    folder?.name && onEditFolderOrFile(folder?.name, undefined, folderUpdated)
                  }
                />
              );
            })}
        </>
      </SkeletonComponent>
    );
  }, [folders, actualFolder]);

  const renderFiles = () => {
    const filesToList = actualFolder ? [...(actualFolder?.files || [])] : [];

    return filesToList
      ?.filter((file) => {
        if (search && file?.name)
          return file?.name?.trim().toLowerCase().includes(search.trim().toLowerCase());

        return true;
      })
      .map((file, index) => {
        return (
          <FileComponent
            key={file.name}
            file={file}
            onOpenCarouselModal={() => {
              showImgModalRef?.current?.handleOpenModal(
                index,
                filesToList.map((file) => ({ url: file.uri ?? "", type: file.type ?? "" }))
              );
            }}
            onDelete={() =>
              actualFolder?.name && onEditFolderOrFile(actualFolder?.name, file?.name)
            }
            onEdit={(fileUpdated) =>
              actualFolder?.name &&
              onEditFolderOrFile(actualFolder?.name, file?.name, undefined, fileUpdated)
            }
          />
        );
      });
  };

  const renderBackButton = () => {
    if (navigation && navigation?.length > 0) {
      return (
        <Button
          data-cy={`button-back-${type}`}
          onClick={onBackFolder}
          customColor={Color.SECONDARY}
          className={classes.button}
        >
          <KeyboardBackspaceIcon className={classes.iconButton} />
          {translate("back")}
        </Button>
      );
    }

    return null;
  };

  const renderUploadButton = () => {
    if (navigation && navigation?.length > 0) {
      return (
        <Button
          customColor={Color.SECONDARY}
          data-cy={`button-upload-${type}`}
          onClick={chooseFile}
          className={classes.button}
        >
          <PublishIcon className={classes.iconButton} />
          {translate("upload")}
        </Button>
      );
    }

    return null;
  };

  const renderInfoNavigation = () => {
    if (navigation && navigation?.length > 0) {
      return (
        <div className={classes.containerInfoNavigation}>
          <IconButton onClick={() => setNavigation([])} size="small" className={classes.iconButton}>
            <HomeIcon htmlColor="#465869" />
          </IconButton>
          <span>{getHistoryOfNavigation()}</span>
        </div>
      );
    }

    return null;
  };

  const renderVirtualFolder = () => {
    if (isCreatingFolder) {
      return (
        <Folder
          isOnViewMode={isOnViewMode}
          folder={{ type }}
          isDisabled={!isCreatingFolder}
          onEdit={(folder: IFolder) => {
            createNewFolderOrFile({ ...folder, type });
          }}
        />
      );
    }

    return null;
  };

  return (
    <Grid container direction="column" alignItems="center" className={classes.mainGrid}>
      <div className={classes.rowGridType}>
        <FoldersListSectionTitle
          isOnViewMode={isOnViewMode}
          name={type}
          allowedExtensions={allowedExtensions}
        />

        <div className={classes.rowGridButtons}>
          {renderBackButton()}
          {!isOnViewMode && canAdd && (
            <>
              <Button
                customColor={Color.SECONDARY}
                data-cy={`button-new-folder-${type}`}
                onClick={() => setIsCreatingFolder(!isCreatingFolder)}
                className={classes.button}
              >
                <AddIcon className={classes.iconButton} />
                {translate("newFolder")}
              </Button>
              {renderUploadButton()}
              <input
                type="file"
                multiple
                ref={hiddenFileInput}
                data-cy={`input-file-${type}`}
                onChange={handleChange}
                className={classes.inputFile}
                accept="image/*, video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
              />
              {/* //TODO: Share folder */}
              {false && (
                <Button
                  customColor={Color.SECONDARY}
                  data-cy={`button-share-${type}`}
                  className={classes.button}
                >
                  <ShareIcon className={classes.iconButton} />
                  {translate("share")}
                </Button>
              )}
              <Input
                value={inputSearch}
                onChange={(event) => setInputSearch(event.target.value)}
                rightIcon={
                  <IconButton
                    className={classes.iconSearchButton}
                    onClick={() => setSearch(inputSearch)}
                    data-cy={`button-search-${type}`}
                  >
                    <SearchIcon />
                  </IconButton>
                }
                onKeyDown={(event) => {
                  if (event.key && event.key.toLowerCase().trim() === "enter") {
                    setSearch(inputSearch);
                  }
                }}
                data-cy={`input-search-${type}`}
              />
            </>
          )}
        </div>
      </div>
      {renderInfoNavigation()}
      <Grid
        item
        container
        direction="row"
        alignItems="center"
        className={classes.gridFolders}
        spacing={1}
        data-cy={`folders-files-list-${type}`}
      >
        {renderVirtualFolder()}
        {renderFolders}
        {renderFiles()}
      </Grid>
      <ImageCarouselModal ref={showImgModalRef} />
    </Grid>
  );
};

export default ListFolders;
